import { Helmet } from 'react-helmet-async';
import { LOCALES, DEFAULT_LOCALE, type Locale } from '../i18n/index';
import { localePath } from '../i18n/locale-path';
import { countryConfig } from '../country';

/**
 * Serialize the JSON-LD payload so scraped tender text can never break out of
 * the <script> block.
 *
 * `JSON.stringify` escapes `"` and `\` but NOT `<`, and react-helmet-async emits
 * script children VERBATIM on the server (`generateTagsAsString` applies
 * `encodeSpecialCharacters` to attributes and <title> only, never to innerHTML).
 * So a tender titled `</script><img src=x onerror=…>` — publishers type the title,
 * and `ungm.adapter.ts` additionally decodes HTML entities into it — would close
 * this tag and inject live markup into every PRERENDERED page carrying that title:
 * `/`, the region and category hubs, and its own detail page, all served as static
 * edge-cached HTML.
 *
 * `<` alone is enough to stop the breakout, and `<` is legal inside a JSON string,
 * so parsers are unaffected. Mirrors `serializeState` in entry-server.tsx, which
 * already does this for `window.__RQ_STATE__`.
 *
 * NOTE: client-side this is moot — helmet assigns innerHTML on a detached
 * <script>, which never executes. Reproduce against a built dist/**\/index.html.
 */
function serializeJsonLd(value: Record<string, unknown>): string {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
}

export interface SeoProps {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
  noIndex?: boolean;
  /** Bare path of the previous page of a paginated listing, if any. */
  prev?: string;
  /** Bare path of the next page of a paginated listing, if any. */
  next?: string;
  lang?: Locale;
  /**
   * The locales this page really exists in, for hreflang (and x-default, which
   * takes the first). Default: every deployment locale — the behaviour of every
   * page before 2026-10-15. A KZ guide page with no Kazakh body passes `['ru']`,
   * so it never advertises a `/kk/…` twin that is only the Russian text again.
   */
  locales?: readonly Locale[];
}

const SITE_NAME = countryConfig.brand.name;
const SITE_URL = countryConfig.brand.siteUrl;
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

const OG_LOCALE: Record<Locale, string> = { uz: 'uz_UZ', ru: 'ru_RU', kk: 'kk_KZ', es: 'es_ES' };

// Tender titles are generated from the lot's subject line, which on real data runs
// to 245 characters (median 132, 88% over 60 chars across a 40-page sample). Google
// truncates the displayed title around 60 characters, so on most of the ~13k tender
// URLs the buyer name — the one thing distinguishing two otherwise identical lots —
// was being cut off entirely, and Google rewrites titles it can't use.
//
// 68 is deliberately not 60: hard-capping the subject at 45 (60 minus the
// " — UzTender.com" suffix) would mangle the hand-written page titles, which are
// already well-formed at 50-68 chars. This cap exists to kill the 132-245 char
// machine-generated outliers, not to police curated copy.
const MAX_TITLE_LENGTH = 68;
// Google renders roughly 155-160 chars of description. The tender generator used a
// hard slice(0, 160) that cut mid-word with no ellipsis ("...предназначена для со").
const MAX_DESCRIPTION_LENGTH = 155;

/**
 * Truncate on a word boundary and append an ellipsis. Returns the input unchanged
 * when it already fits, so short/curated strings are never touched.
 */
export function truncateOnWordBoundary(text: string, max: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;

  // Reserve one char for the ellipsis, then walk back to the last space so we never
  // cut mid-word. If there's no space in range (one very long token), hard-cut —
  // better a clipped word than a 245-char title.
  //
  // A space INSIDE a grouped number («1 232 000», «2 830 лотов») is not a word
  // boundary: cutting there left descriptions ending in «Бюджет: 1 232…» (GSC
  // audit 2026-09-07). Bind digit groups with a placeholder before looking for
  // the last space, so a figure is either kept whole or dropped whole.
  const GROUP = '\uE000'; // private-use char: never in real copy, lint-clean (no-control-regex)
  const bound = trimmed.replace(/(\d)[ \u00a0\u202f\u2009](?=\d)/g, `$1${GROUP}`);
  const slice = bound.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.replace(/\uE000/g, ' ').replace(/[\s,;:.—-]+$/, '')}…`;
}

export function Seo({
  title,
  description,
  canonical,
  ogImage,
  jsonLd,
  noIndex,
  prev,
  next,
  lang = DEFAULT_LOCALE,
  locales = LOCALES,
}: SeoProps) {
  const shortTitle = truncateOnWordBoundary(title, MAX_TITLE_LENGTH);
  const fullTitle = shortTitle === SITE_NAME ? shortTitle : `${shortTitle} — ${SITE_NAME}`;
  const shortDescription = description
    ? truncateOnWordBoundary(description, MAX_DESCRIPTION_LENGTH)
    : undefined;

  // `canonical` is the BARE (ru, DEFAULT_LOCALE) route path. Build a per-locale
  // absolute URL: ru -> bare, uz -> /uz-prefixed. Canonical points at THIS page's
  // locale (self); hreflang lists both languages' distinct URLs.
  const selfUrl = canonical ? `${SITE_URL}${localePath(canonical, lang)}` : undefined;
  const image = ogImage ?? DEFAULT_OG_IMAGE;

  const alternates = canonical
    ? locales.map((l) => ({ lang: l, href: `${SITE_URL}${localePath(canonical, l)}` }))
    : [];
  // x-default points at the UZBEK version, not the ru bare root: Uzbek is the
  // primary demand language («davlat xaridlari» ~14.8k/mo vs Russian tender terms
  // ~20–90/mo), so an undetermined-language searcher should land on uz.
  const defaultHref = canonical ? `${SITE_URL}${localePath(canonical, locales[0])}` : undefined;

  return (
    <Helmet defer={false}>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {shortDescription && <meta name="description" content={shortDescription} />}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      {selfUrl && <link rel="canonical" href={selfUrl} />}
      {/* Google retired rel=next/prev as an INDEXING signal in 2019, but both are
          still followed as ordinary links, and Bing/Yandex still consume them.
          Same-locale, since a pager never crosses languages. */}
      {prev && <link rel="prev" href={`${SITE_URL}${localePath(prev, lang)}`} />}
      {next && <link rel="next" href={`${SITE_URL}${localePath(next, lang)}`} />}
      {alternates.map(({ lang: l, href }) => (
        <link key={l} rel="alternate" hrefLang={l} href={href} />
      ))}
      {defaultHref && <link rel="alternate" hrefLang="x-default" href={defaultHref} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {shortDescription && <meta property="og:description" content={shortDescription} />}
      <meta property="og:site_name" content={SITE_NAME} />
      {selfUrl && <meta property="og:url" content={selfUrl} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      {locales
        .filter((l) => l !== lang)
        .map((l) => (
          <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {shortDescription && <meta name="twitter:description" content={shortDescription} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd && <script type="application/ld+json">{serializeJsonLd(jsonLd)}</script>}
    </Helmet>
  );
}
